import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ElementRef, OnInit, signal, ViewChild, ViewEncapsulation } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { FieldValueInterface, FormInputComponent, InlineEditService, ObjectType } from '@galaxy/crm/static';
import { Market } from '../../../core/markets/market';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { map, Observable, take, tap } from 'rxjs';
import { CRMMarketContext, PartnerCRMSalespersonMarketService } from '../../salesperson-market.service';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { ALL_MARKETS_DEFAULT } from '../../../constants';

const NO_VALUE_FOR_MARKET = '<NO_VALUE_FOR_MARKET>';

@Component({
  templateUrl: './group-id-form-input.component.html',
  providers: [],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GalaxyFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    MatIcon,
    MatIconButton,
  ],
  styleUrls: ['../shared.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class GroupIdFormInputComponent extends FormInputComponent implements OnInit {
  @ViewChild('input') input: ElementRef<HTMLInputElement>;

  NO_VALUE_FOR_MARKET = NO_VALUE_FOR_MARKET;

  readOnly = false;
  objectType: ObjectType = 'Contact';
  _control: FormControl<Market | string> = new FormControl<Market | string>('', {
    nonNullable: true,
    validators: [Validators.required],
  });
  _label = '';
  isInline$: Observable<boolean>;

  set control(control: FormControl<Market | string>) {
    this._control = control;
    this._control.addValidators(Validators.required);
    this._control.addValidators(this.isMarketValid.bind(this));
    this._control.setValue(this._control.value || '');
  }

  get control(): UntypedFormControl {
    return this._control;
  }

  set formGroup(group: UntypedFormGroup) {
    // Do nothing
  }

  _fieldId = signal('');
  set fieldId(fieldId: string) {
    this._fieldId.set(fieldId);
  }

  get hasModification(): boolean {
    return this._control.dirty;
  }

  set label(label: string) {
    this._label = label;
  }

  get label(): string {
    return this._label;
  }

  get value(): FieldValueInterface {
    const controlValue: Market | string = this._control.value;
    if (typeof controlValue === 'string') {
      return {
        fieldId: this._fieldId(),
        stringValue: controlValue,
      };
    }
    return {
      fieldId: this._fieldId(),
      stringValue: controlValue?.market_id,
    };
  }

  loadingValues = signal(true);
  availableOptions = toSignal(
    this.marketsService.userAccessibleMarkets$.pipe(
      map((markets) => markets.filter((m: Market) => m.market_id !== ALL_MARKETS_DEFAULT)),
      tap((_) => {
        this.loadingValues.set(false);
      }),
    ),
  );
  filteredOptions: Market[] = [];

  constructor(
    private readonly marketsService: PartnerCRMSalespersonMarketService,
    private readonly destroyRef: DestroyRef,
    private readonly inlineEditService: InlineEditService,
  ) {
    super();
    this.isInline$ = this.inlineEditService.isInlineEdit$;
  }

  ngOnInit(): void {
    if (this._control.value instanceof Market) {
      this.marketsService.selectMarket(CRMMarketContext.EditContext, this._control.value);
    } else if (this._control.value) {
      this.marketsService.userAccessibleMarkets$
        .pipe(
          take(1),
          tap((accessibleMarkets) => {
            const valueAsString = this._control?.value as string;
            this.marketsService.selectMarketByID(
              CRMMarketContext.EditContext,
              valueAsString ?? accessibleMarkets[0].market_id,
            );
          }),
        )
        .subscribe();
    }
  }

  private extractMarketIdFromValue(value: Market | string): string {
    let marketID: string;
    if (value && typeof value === 'object') {
      marketID = value.market_id;
    } else if (typeof value === 'string') {
      marketID = value;
    }
    return marketID;
  }

  private isMarketValid(control: AbstractControl): ValidationErrors | null {
    const marketId = this.extractMarketIdFromValue(control.value);
    const validMarket = !!this.availableOptions().find((s) => s.market_id === marketId);
    if (validMarket || !marketId) {
      return null;
    }
    return {
      invalidMarket: true,
    };
  }

  displayFn(availableOptions: Market[]): (market: Market | string) => string {
    return (market: Market | string): string => {
      if (!market) {
        return '';
      }
      if (typeof market === 'string') {
        const foundMarket = availableOptions.find((sp) => sp.market_id === market);
        return foundMarket?.name || market;
      }
      return market?.name;
    };
  }

  marketFilterFn(market: Market, searchTerm: string): boolean {
    return (
      !searchTerm ||
      market.name?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      market.market_id?.toLowerCase().includes(searchTerm?.toLowerCase())
    );
  }

  clear(): void {
    this._control.reset();
  }

  filter(): void {
    const filterValue = this.input.nativeElement.value.toLowerCase();
    this.filteredOptions = this.availableOptions().filter((o) => this.marketFilterFn(o, filterValue));
  }

  saveField(): void {
    this.inlineEditService.saveInlineEdit(this.objectType, [this.value]);
  }

  onOptionSelected($event: Market | string) {
    if ($event instanceof Market) {
      this.marketsService.selectMarket(CRMMarketContext.EditContext, $event);
    }
  }
}
